import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import styled from "styled-components";
import { RotatingLines } from "react-loader-spinner";
import BottomModalV2, { RefProps } from "../common/BottomModalV2";
import Box from "../common/Box";
import Button from "../common/Button";
import CheckBox from "../common/CheckBox";
import { useExecuteFeedbackTemplateMutation, usePreviewExecuteFeedbackMutation, useRegisterExecuteFeedbackMutation } from "../../api/noticeAPI";
import SVG from "src/utils/SVG";
import {
  EXCEL_UPLOAD_MAX_SIZE,
  EXCEL_UPLOAD_SIZE_MESSAGE,
  EXCEL_UPLOAD_TYPE_MESSAGE,
  EXECUTE_FEEDBACK_CAUTION_VERDICTS,
  EXECUTE_FEEDBACK_EMPTY_VERDICT_LABEL,
  EXECUTE_FEEDBACK_GUIDE,
  EXECUTE_FEEDBACK_UNMATCHED_REASON_LABEL,
  isExcelUploadFile,
} from "../../constants/notice";
import { getUploadErrorMessage } from "../../utils/excelUploadErrorMessage";

interface IExecuteFeedbackUploadModalProps {
  noticeIdx: string;
  // 등록이 끝나면 부모가 이행목록을 다시 불러온다
  onRegistered: () => void;
}

const verdictLabel = (value: string) => (value === "" ? EXECUTE_FEEDBACK_EMPTY_VERDICT_LABEL : value);

/**
 * 이행평가 피드백 엑셀 업로드 (VMD-389)
 * 파일 선택 → 미리보기(판정별 건수, 매장별 코멘트) → 전달할 판정 선택 → 등록.
 * 서버는 상태를 들고 있지 않으므로 판정을 바꿀 때마다 같은 파일로 미리보기를 다시 요청한다.
 */
const ExecuteFeedbackUploadModal = (props: IExecuteFeedbackUploadModalProps, ref: React.Ref<RefProps>) => {
  const modalRef = useRef<RefProps>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ExecuteFeedbackPreview | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewFeedback, { isLoading: isPreviewing }] = usePreviewExecuteFeedbackMutation();
  const [registerFeedback, { isLoading: isRegistering }] = useRegisterExecuteFeedbackMutation();
  const [downloadTemplate, { isLoading: isTemplateDownloading }] = useExecuteFeedbackTemplateMutation();
  const isBusy = isPreviewing || isRegistering;

  // 닫을 때는 초기화하지 않는다. BottomModalV2 가 닫힘 애니메이션 동안 children 을 그리므로 다음 modalOpen 에서 초기화한다.
  const resetState = () => {
    setFile(null);
    setPreview(null);
    setSelected([]);
    setExpanded(null);
    setErrorMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useImperativeHandle(ref, () => ({
    modalOpen: () => {
      resetState();
      modalRef.current?.modalOpen();
    },
    modalClose: () => modalRef.current?.modalClose(),
  }));

  const buildFormData = (target: File, verdicts?: string[]) => {
    const formData = new FormData();
    formData.append("file", target);
    if (verdicts) formData.append("verdicts", JSON.stringify(verdicts));
    return formData;
  };

  // 템플릿 다운로드 - DATA_UPLOAD 시트 헤더를 요청자가 받아 쓰도록 (VMD-404). 파일명은 API 레이어에서 Content-Disposition 기준으로 저장
  const handleDownloadTemplate = async () => {
    if (isTemplateDownloading) return;
    try {
      await downloadTemplate().unwrap();
    } catch (error) {
      console.error("피드백 템플릿 다운로드 실패:", error);
      setErrorMessage("템플릿 다운로드에 실패했습니다. 잠시 후 다시 시도해주세요.");
    }
  };

  // 실패하면 false 를 돌려준다. 호출부가 낙관적으로 바꾼 선택을 되돌릴 수 있도록.
  const requestPreview = async (target: File, verdicts?: string[]): Promise<boolean> => {
    try {
      const data = await previewFeedback({ notice_idx: props.noticeIdx, formData: buildFormData(target, verdicts) }).unwrap();
      setPreview(data);
      setSelected(data.selectedVerdicts);
      setErrorMessage("");
      return true;
    } catch (error) {
      setErrorMessage(getUploadErrorMessage(error));
      return false;
    }
  };

  // 파일 선택 - 확장자·5MB 사전 검증 후 바로 미리보기
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = "";
    if (!picked) return;
    if (!isExcelUploadFile(picked.name)) {
      setErrorMessage(EXCEL_UPLOAD_TYPE_MESSAGE);
      return;
    }
    if (picked.size > EXCEL_UPLOAD_MAX_SIZE) {
      setErrorMessage(EXCEL_UPLOAD_SIZE_MESSAGE);
      return;
    }
    setFile(picked);
    setPreview(null);
    setExpanded(null);
    requestPreview(picked);
  };

  const toggleVerdict = async (value: string) => {
    if (!file || isBusy) return;
    const prev = selected;
    const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
    setSelected(next);
    // 미리보기가 실패하면 선택을 되돌려 요약 수치(선택 기준)와 미리보기(서버 응답 기준)가 어긋나지 않게 한다.
    if (!(await requestPreview(file, next))) setSelected(prev);
  };

  const handleRegister = async () => {
    if (!file || !preview || isBusy) return;
    const existingCount = preview.stores.filter((s) => s.contents !== null && s.hasExistingFeedback).length;
    // 파일에 들어왔지만 이번에 전달할 행이 없는 매장의 기존 피드백은 교체 범위라 삭제된다.
    const removedCount = preview.stores.filter((s) => s.contents === null && s.hasExistingFeedback).length;
    const message =
      `${preview.targetStoreCount}개 매장에 피드백 코멘트를 등록하고 평가완료로 표시합니다.` +
      (existingCount > 0 ? `\n이 중 ${existingCount}개 매장은 기존 피드백이 교체됩니다.` : "") +
      (removedCount > 0 ? `\n전달할 항목이 없는 ${removedCount}개 매장은 기존 피드백이 삭제됩니다.` : "") +
      "\n등록하시겠습니까?";
    if (!window.confirm(message)) return;

    try {
      const formData = buildFormData(file, selected);
      formData.append("notify", "false");
      const result = await registerFeedback({ notice_idx: props.noticeIdx, formData }).unwrap();
      alert(`${result.registered}개 매장에 피드백을 등록했습니다.` + (result.replaced > 0 ? ` (기존 ${result.replaced}건 교체)` : ""));
      modalRef.current?.modalClose();
      props.onRegistered();
    } catch (error) {
      setErrorMessage(getUploadErrorMessage(error));
    }
  };

  const targetStores = preview ? preview.stores.filter((s) => s.contents !== null) : [];
  const canRegister = !!preview && preview.targetStoreCount > 0 && preview.overLimitStoreCount === 0 && !isBusy;
  const cautionMessages = selected.filter((v) => EXECUTE_FEEDBACK_CAUTION_VERDICTS[v]).map((v) => EXECUTE_FEEDBACK_CAUTION_VERDICTS[v]);

  return (
    <BottomModalV2 ref={modalRef} modalTitle="이행평가 피드백 업로드" onClose={() => !isBusy} onConfirm={() => undefined}>
      <Box pd="20px 15px">
        {preview === null && (
          <GuideBox>
            <p className="guide-title">업로드 규칙</p>
            <ul>
              {EXECUTE_FEEDBACK_GUIDE.map((guide, idx) => (
                <li key={idx}>{guide}</li>
              ))}
            </ul>
          </GuideBox>
        )}

        <FileRow>
          <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleFileChange} />
          {/* 템플릿은 업로드와 무관한 빈 파일 받기라 미리보기·등록 중(isBusy)에도 막지 않는다 */}
          <Button
            variant="outlined"
            br="round"
            color="secondary"
            size="medium"
            className="file-btn template-btn"
            onClick={handleDownloadTemplate}
            disabled={isTemplateDownloading}
          >
            <SVG name="fileDownload" width={16} height={16} viewBox="0 0 20 20" />
            {isTemplateDownloading ? "다운로드 중..." : "템플릿 다운로드"}
          </Button>
          <Button variant="outlined" br="round" size="medium" className="file-btn" onClick={() => !isBusy && fileInputRef.current?.click()}>
            {file ? "다른 파일 선택" : "파일 선택"}
          </Button>
          <span className={file ? "file-name" : "file-name empty"}>{file ? file.name : "선택된 파일 없음"}</span>
        </FileRow>

        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

        {isBusy && (
          <Loading>
            <RotatingLines width="28" strokeColor="#00263e" strokeWidth="3" />
            <span>{isRegistering ? "등록 중입니다..." : "파일을 확인하고 있습니다..."}</span>
          </Loading>
        )}

        {preview && (
          <>
            <SectionTitle>전달할 판정</SectionTitle>
            {preview.hasVerdictColumn ? (
              <VerdictList>
                {preview.verdicts.map((verdict) => (
                  <li key={verdict.value}>
                    <CheckBox
                      id={`verdict-${verdict.value || "empty"}`}
                      type="round"
                      checked={selected.includes(verdict.value)}
                      disabled={isBusy}
                      onChange={() => toggleVerdict(verdict.value)}
                      label={`${verdictLabel(verdict.value)} ${verdict.count}건`}
                    />
                  </li>
                ))}
              </VerdictList>
            ) : (
              <Note>판정 컬럼이 없어 모든 행을 전달합니다.</Note>
            )}
            {cautionMessages.map((msg, idx) => (
              <Note key={idx} className="warning">
                {msg}
              </Note>
            ))}

            <SummaryBox>
              <li className="matched">
                <span className="label">등록 매장</span>
                <span className="count">{preview.targetStoreCount}</span>
              </li>
              <li>
                <span className="label">공통 피드백</span>
                <span className="count">{selected.reduce((sum, v) => sum + (preview.commonVerdictCounts[v] ?? 0), 0)}</span>
              </li>
              <li className="unmatched">
                <span className="label">매칭 실패 행</span>
                <span className="count">{preview.unmatched.length}</span>
              </li>
            </SummaryBox>

            {preview.overLimitStoreCount > 0 && (
              <Note className="warning">
                코멘트가 {preview.maxContentLength}자를 넘는 매장이 {preview.overLimitStoreCount}곳 있어 등록할 수 없습니다. 아래 목록에서 확인해 주세요.
              </Note>
            )}

            {targetStores.length > 0 && (
              <>
                <SectionTitle>매장별 코멘트 미리보기</SectionTitle>
                <StoreList>
                  {targetStores.map((store) => (
                    <li key={store.executeIdx} className={store.overLimit ? "over" : ""}>
                      <button type="button" className="store-row" onClick={() => setExpanded(expanded === store.executeIdx ? null : store.executeIdx)}>
                        <span className="code">{store.storeId}</span>
                        <span className="name">{store.storeName}</span>
                        <span className="meta">
                          {store.targetRows}건{store.hasExistingFeedback ? " · 교체" : ""}
                          {store.overLimit ? " · 길이 초과" : ""}
                        </span>
                      </button>
                      {expanded === store.executeIdx && (
                        <pre className="contents">
                          {store.contents}
                          <span className="length">
                            {store.contents?.length ?? 0} / {preview.maxContentLength}자
                          </span>
                        </pre>
                      )}
                    </li>
                  ))}
                </StoreList>
              </>
            )}

            {preview.unmatched.length > 0 && (
              <>
                <SectionTitle>매칭 실패 행</SectionTitle>
                <FailTable>
                  <thead>
                    <tr>
                      <th className="row">행</th>
                      <th>매장코드 / 매장명</th>
                      <th>사유</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.unmatched.map((item, idx) => (
                      <tr key={idx}>
                        <td className="row">{item.row}</td>
                        <td className="value">{[item.storeCode || "-", item.storeName].filter(Boolean).join(" / ")}</td>
                        <td>{EXECUTE_FEEDBACK_UNMATCHED_REASON_LABEL[item.reason] ?? item.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </FailTable>
              </>
            )}

            {preview.targetStoreCount === 0 && <Note className="warning">선택한 판정에 해당하는 매장 피드백이 없습니다.</Note>}

            <RegisterBox>
              <Button variant="contained" br="round" onClick={handleRegister} disabled={!canRegister}>
                {preview.targetStoreCount}개 매장에 등록
              </Button>
            </RegisterBox>
          </>
        )}
      </Box>
    </BottomModalV2>
  );
};

const GuideBox = styled.div`
  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.color.bgPrimary};
  .guide-title {
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.color.primary};
  }
  > ul > li {
    position: relative;
    padding-left: 10px;
    font-size: 12px;
    line-height: 18px;
    color: ${({ theme }) => theme.color.secondary};
    word-break: keep-all;
    &::before {
      content: "";
      position: absolute;
      top: 8px;
      left: 0;
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background-color: ${({ theme }) => theme.color.tertiary};
    }
  }
`;

const FileRow = styled.div`
  ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start")}
  > input[type="file"] {
    display: none;
  }
  .file-btn {
    flex: 0 0 auto;
    width: auto;
  }
  .template-btn {
    ${({ theme }) => theme.flexBox.flexMixin("row", "center")};
    margin-right: 8px;
  }
  .file-name {
    flex: 1 1 auto;
    margin-left: 10px;
    ${({ theme }) => theme.textOverflow.singleLine()}
    font-size: 14px;
    color: ${({ theme }) => theme.color.primary};
    &.empty {
      color: ${({ theme }) => theme.color.tertiary};
    }
  }
`;

const ErrorText = styled.p`
  margin-top: 10px;
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.color.warning};
  word-break: keep-all;
`;

const Loading = styled.div`
  ${({ theme }) => theme.flexBox.flexMixin("row", "center")}
  height: 40px;
  margin-top: 12px;
  > span {
    margin-left: 10px;
    font-size: 14px;
    color: ${({ theme }) => theme.color.secondary};
  }
`;

const SectionTitle = styled.p`
  margin: 20px 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.primary};
`;

const VerdictList = styled.ul`
  > li {
    padding: 6px 0;
  }
`;

const Note = styled.p`
  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
  color: ${({ theme }) => theme.color.secondary};
  word-break: keep-all;
  &.warning {
    color: ${({ theme }) => theme.color.warning};
  }
`;

const SummaryBox = styled.ul`
  ${({ theme }) => theme.flexBox.flexMixin("row", "space-around", "stretch")}
  padding: 14px 0;
  margin-top: 16px;
  border: 1px solid ${({ theme }) => theme.color.linePrimary};
  border-radius: 8px;
  > li {
    flex: 1 1 0;
    ${({ theme }) => theme.flexBox.flexMixin("column", "center")}
    &:not(:first-child) {
      border-left: 1px solid ${({ theme }) => theme.color.linePrimary};
    }
    .label {
      font-size: 12px;
      color: ${({ theme }) => theme.color.tertiary};
    }
    .count {
      margin-top: 4px;
      font-family: "Poppins";
      font-size: 22px;
      color: ${({ theme }) => theme.color.primary};
    }
    &.matched .count {
      color: ${({ theme }) => theme.color.subMain};
    }
    &.unmatched .count {
      color: ${({ theme }) => theme.color.warning};
    }
  }
`;

const StoreList = styled.ul`
  max-height: 320px;
  overflow-y: auto;
  border-top: 1px solid ${({ theme }) => theme.color.linePrimary};
  > li {
    border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
    &.over .meta {
      color: ${({ theme }) => theme.color.warning};
    }
  }
  .store-row {
    ${({ theme }) => theme.flexBox.flexMixin("row", "flex-start")}
    width: 100%;
    padding: 10px 4px;
    font-size: 13px;
    text-align: left;
    background: none;
    border: 0;
    cursor: pointer;
    .code {
      flex: 0 0 64px;
      font-family: "Poppins";
      color: ${({ theme }) => theme.color.secondary};
    }
    .name {
      flex: 1 1 auto;
      ${({ theme }) => theme.textOverflow.singleLine()}
      color: ${({ theme }) => theme.color.primary};
    }
    .meta {
      flex: 0 0 auto;
      margin-left: 8px;
      font-size: 12px;
      color: ${({ theme }) => theme.color.tertiary};
    }
  }
  .contents {
    position: relative;
    margin: 0 4px 10px;
    padding: 10px 12px 24px;
    border-radius: 8px;
    background-color: ${({ theme }) => theme.color.bgPrimary};
    font-family: inherit;
    font-size: 12px;
    line-height: 18px;
    white-space: pre-wrap;
    word-break: break-all;
    color: ${({ theme }) => theme.color.primary};
    .length {
      position: absolute;
      right: 10px;
      bottom: 6px;
      font-size: 11px;
      color: ${({ theme }) => theme.color.tertiary};
    }
  }
`;

const FailTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px 6px;
    border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
    text-align: left;
    vertical-align: top;
    line-height: 18px;
    word-break: break-all;
  }
  th {
    background-color: ${({ theme }) => theme.color.bgPrimary};
    font-weight: 500;
    color: ${({ theme }) => theme.color.secondary};
  }
  .row {
    width: 40px;
    text-align: center;
  }
  td.row {
    font-family: "Poppins";
    color: ${({ theme }) => theme.color.primary};
  }
  td.value {
    color: ${({ theme }) => theme.color.primary};
  }
  td:last-child {
    color: ${({ theme }) => theme.color.warning};
  }
`;

const RegisterBox = styled.div`
  margin-top: 20px;
`;

export default forwardRef(ExecuteFeedbackUploadModal);
